import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  signal,
  inject,
  viewChild,
} from '@angular/core';
import { ButtonUIComponent, IconUIComponent } from '@shared-ui-kit';
import { WebMenuButton } from '../web-menu-button/web-menu-button';
import { WebLogo } from '../web-logo/web-logo';
import { DOCUMENT, NgClass } from '@angular/common';
import { WebMenuButtonMenu } from '../web-menu-button-menu/web-menu-button-menu';
import { ActivatedRoute, isActive, IsActiveMatchOptions, Router } from '@angular/router';

import { TranslatePipe } from '@ngx-translate/core';
import { AuthService, AuthContextEnum } from '@feature-auth';
import { LandingRouteData } from '../landing-layout/landing-route-data';
import { LandingScroller } from '../_services/landing-scroller';

const EXACT_PATH: Partial<IsActiveMatchOptions> = { paths: 'exact', queryParams: 'ignored' };

@Component({
  selector: 'fr8bot-web-header',
  templateUrl: './web-header.html',
  styleUrl: './web-header.scss',
  imports: [
    IconUIComponent,
    WebMenuButton,
    WebLogo,
    ButtonUIComponent,
    NgClass,
    WebMenuButtonMenu,
    TranslatePipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WebHeader implements OnDestroy {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private authService = inject(AuthService);
  private document = inject(DOCUMENT);
  private readonly header = viewChild.required<ElementRef<HTMLElement>>('header');

  isMenuOpen = signal(false);
  protected readonly menuToggled = signal(false);

  protected readonly isHomeActive = isActive('/', this.router, EXACT_PATH);
  protected readonly isShippersActive = isActive('/shippers', this.router, EXACT_PATH);
  protected readonly isDriversActive = isActive('/drivers', this.router, EXACT_PATH);

  constructor() {
    // Anchors scrolled to on the landing pages stop below this fixed header.
    inject(LandingScroller).trackHeader(() => this.header().nativeElement);
  }

  openMenu() {
    const nextValue = !this.isMenuOpen();

    this.menuToggled.set(true);
    this.isMenuOpen.set(nextValue);

    if (nextValue) {
      this.lockPageScroll();
    } else {
      this.unlockPageScroll();
    }
  }

  public signIn(): void {
    this.authService.authenticate(AuthContextEnum.SignIn);
  }

  public createAccount(): void {
    // The header sits in LandingLayout, so the page it opens the account for is the routed child.
    const data = this.route.snapshot.firstChild?.data as Partial<LandingRouteData> | undefined;

    this.authService.authenticate(data?.createAccountContext ?? AuthContextEnum.CreateAccount);
  }

  private lockPageScroll(): void {
    this.document.documentElement.classList.add('web-menu-open');
    this.document.body.classList.add('web-menu-open');
  }

  private unlockPageScroll(): void {
    this.document.documentElement.classList.remove('web-menu-open');
    this.document.body.classList.remove('web-menu-open');
  }

  ngOnDestroy() {
    this.unlockPageScroll();
  }
}
